import React, { useState, useCallback } from 'react'
import { useTranslation, useCurrentLanguage } from '../hooks/useTranslation'
import { buildVivaSharePayload } from '../lib/viva-share.js'

async function copyShareText (text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

export default function FooterShareButton () {
  const t = useTranslation()
  const language = useCurrentLanguage()
  const [status, setStatus] = useState('idle')

  const handleShare = useCallback(async () => {
    const payload = buildVivaSharePayload(t, language)

    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({
          title: payload.title,
          text: payload.shareText,
          url: payload.url,
        })
        setStatus('shared')
      } else {
        await copyShareText(payload.clipboardText)
        setStatus('copied')
      }
    } catch (err) {
      if (err?.name === 'AbortError') return
      try {
        await copyShareText(payload.clipboardText)
        setStatus('copied')
      } catch {
        setStatus('error')
      }
    }

    window.setTimeout(() => setStatus('idle'), 2500)
  }, [t, language])

  const label =
    status === 'copied'
      ? t('common.footer.shareCopied')
      : status === 'shared'
        ? t('common.footer.shareShared')
        : status === 'error'
          ? t('common.footer.shareError')
          : t('common.footer.share')

  return (
    <button
      type="button"
      onClick={handleShare}
      className="inline-flex min-h-11 touch-manipulation items-center gap-2 rounded-lg border border-gray-600 bg-gray-700/80 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:border-primary-golden/60 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-golden focus:ring-offset-2 focus:ring-offset-gray-800"
      aria-label={t('common.footer.shareAria')}
    >
      <svg className="h-5 w-5 shrink-0 text-primary-golden" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
        />
      </svg>
      <span>{label}</span>
    </button>
  )
}
